import { apiCall } from '../../services/apiUtil';
import { uploadFile } from '../../services/imageUploadService';
import { resolveImageUrl } from '../hooks/useProfileImage';

/**
 * Service for handling profile image uploads via the image handler (with Rekognition moderation).
 */
class ProfileImageService {
  /**
   * Upload a profile image for the current user.
   * Routes through the image handler for Rekognition moderation before saving to S3.
   *
   * @param {File|Blob} file   - The image file/blob to upload
   * @param {string|number} userId - The user ID
   * @returns {Promise<{ key: string, url: string }>}
   * @throws {ContentPolicyError} when Rekognition rejects the image
   */
  async uploadProfileImage(file, userId) {
    // Upload via image handler — moderation runs synchronously
    const result = await uploadFile(
      file,
      'profile_images',
      'profile.jpeg',
      `${userId}/profile.jpeg`
    );

    // Persist the approved image path to the user record
    await apiCall(`/user/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ profileImage: result.url }),
    });

    return result;
  }

  /**
   * Get the current profile image URL for a user.
   */
  async getProfileImageUrl(userId) {
    try {
      const userData = await apiCall(`/user/${userId}`);
      const profilePicture = userData?.profileImage;

      if (!profilePicture) return null;

      if (profilePicture.startsWith('/images/')) return resolveImageUrl(profilePicture);
      if (profilePicture.startsWith('http')) return null;

      // Legacy fallback: bare filename — build /images/ path (served by CloudFront in prod, Core API in dev)
      return resolveImageUrl(`/images/profile_images/${userId}/${profilePicture}`);
    } catch (error) {
      console.error('[ProfileImageService] Error getting profile image URL:', error);
      return null;
    }
  }
}

export default new ProfileImageService();
